import { Fragment, FunctionComponent, useMemo } from 'react';
import './styles/ScheduleTable.scss';

import { IDanceClass, IScheduleTableFilters, SCHEDULE_MODE, SCREEN_SIZE, DAY_OF_WEEK, IDanceSchool } from '../models';
import { classNames, isAgeValidForClass, formatDanceClassAgeString, formatDanceClassTime, formatDanceClassNameWithoutAge, formatDanceClassInclusiveFee } from '../utils';
import { Checkbox } from './core/Checkbox';

interface IScheduleWeek {
  dayOfWeek: DAY_OF_WEEK;
  classes: IDanceClass[];
}

interface IScheduleTableProps {
  mode: SCHEDULE_MODE,
  danceSchool: IDanceSchool;
  classes: IDanceClass[];
  selectedClasses: IDanceClass[];
  classClicked: (classDetails: IDanceClass) => void;
  filters: IScheduleTableFilters;
  screenSize: SCREEN_SIZE;
  disabled?: boolean;
}
export const ScheduleTable: FunctionComponent<IScheduleTableProps> = (props: IScheduleTableProps) => {

  const {
    mode,
    danceSchool,
    classes,
    selectedClasses,
    classClicked,
    filters,
    screenSize,
    disabled
  } = props;

  const totalColumns = useMemo(() => {
    return mode === SCHEDULE_MODE.ENROL ? 5 : mode === SCHEDULE_MODE.TRIAL ? 4 : 3;
  }, [mode]);

  const filteredClasses = useMemo(() => {
    let filteredResults = [...classes];
    if (filters.style) {
      filteredResults = filteredResults.filter(x => x.danceStyle === filters.style);
    }
    if (filters.dayOfWeek) {
      filteredResults = filteredResults.filter(x => x.dayOfWeek === filters.dayOfWeek);
    }
    if (filters.age && !isNaN(parseInt(filters.age)) && parseInt(filters.age) > 0) {
      const parsedAge = parseInt(filters.age);

      filteredResults = filteredResults.filter(x => isAgeValidForClass(x, parsedAge));
    }

    return filteredResults;
  }, [filters, classes]);

  const weeklyClasses = useMemo<IScheduleWeek[]>(() => {
    const results: IScheduleWeek[] = [];

    for (const c of filteredClasses) {
      let dayClasses = results.find(x => x.dayOfWeek === c.dayOfWeek);
      if (!dayClasses) {
        dayClasses = {
          dayOfWeek: c.dayOfWeek,
          classes: []
        };
        results.push(dayClasses);
      }

      dayClasses.classes.push(c);
    }
    return results;
  }, [filteredClasses]);

  return (
    <div className={classNames('ppa-schedule-table', screenSize)}>
      <table>
        <thead>
          <tr>
            {(mode === SCHEDULE_MODE.ENROL || mode === SCHEDULE_MODE.TRIAL) && (
              <th></th>
            )}
            <th>Time</th>
            <th>Class</th>
            <th>Age</th>
            {mode === SCHEDULE_MODE.ENROL && (
              <th>Term Fee</th>
            )}
          </tr>
        </thead>
        <tbody>
          {weeklyClasses.length < 1 ? (
            <tr>
              <td colSpan={totalColumns} className='no-data-row'>
                <i>No classes available...</i>
              </td>
            </tr>
          ) : weeklyClasses.map((weekClasses) => (
            <Fragment key={weekClasses.dayOfWeek}>
              <tr>
                <td colSpan={totalColumns} className='day-of-week-header'>
                  {weekClasses.dayOfWeek.toUpperCase()}
                </td>
              </tr>

              {weekClasses.classes.map((availableClass, index) => (
                <tr
                  key={index}
                  onClick={() => disabled ? null : classClicked(availableClass)}
                  className={classNames({
                    selected: selectedClasses.includes(availableClass),
                    disabled
                  })}
                >
                  {(mode === SCHEDULE_MODE.ENROL || mode === SCHEDULE_MODE.TRIAL) && (
                    <td>
                      <Checkbox
                        checked={selectedClasses.includes(availableClass)}
                        size={screenSize === SCREEN_SIZE.SMALL ? 'small' : 'medium'}
                        readOnly
                      />
                    </td>
                  )}
                  <td>{formatDanceClassTime(availableClass)}</td>
                  <td>{formatDanceClassNameWithoutAge(availableClass)} {availableClass.inviteOnly ? '(Invite Only)' : ''}</td>
                  <td className='no-wrap'>{formatDanceClassAgeString(availableClass)}</td>
                  {mode === SCHEDULE_MODE.ENROL && (
                    <td>
                      {formatDanceClassInclusiveFee(availableClass, danceSchool.countryCode)}
                    </td>
                  )}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}